import * as R from 'rambdax'
import {useMemo, useCallback} from 'react'
import {useLocation, useNavigate, Location, Path} from 'react-router-dom'
import {ensureArray} from 'tizra'
import {useEqualBy} from './useEqualBy'

const locationToPath: (loc: Location) => Path = R.pick([
  'pathname',
  'search',
  'hash',
])

export interface SetPrefixedParamsOptions {
  method?: 'push' | 'replace'
  location?: Partial<Path> | ((loc: Partial<Path>) => Partial<Path>)
  kill?: string[]
}

/**
 * React hook for getting and setting query parameters from the router.
 */
export const usePrefixedParams = (prefix: string) => {
  const current = locationToPath(useLocation())
  const navigate = useNavigate()

  let params = useMemo(() => {
    const pairs = Array.from(new URLSearchParams(current.search).entries())
    return Object.fromEntries(
      pairs
        .filter(([k]) => k.startsWith(prefix))
        .map(([k, v]) => [k.replace(prefix, ''), v]),
    )
  }, [current.search, prefix])

  params = useEqualBy(R.equals, params)

  // kill is for removing classic search params (context, searchText)
  const setParams = useCallback(
    (
      params: Record<string, string | string[]>,
      {
        method = 'push',
        location: override,
        kill = [],
      }: SetPrefixedParamsOptions = {},
    ) => {
      const usp = new URLSearchParams(current.search)
      const deleteKeys = Array.from(usp.keys()).filter(
        k => k.startsWith(prefix) || kill.includes(k),
      )
      for (const k of deleteKeys) {
        usp.delete(k)
      }
      for (const k of Object.keys(params).sort()) {
        for (const v of ensureArray(params[k])) {
          usp.append(`${prefix}${k}`, v)
        }
      }
      const search = usp.toString()
      let newLocation: Partial<Path> = {
        hash: current.hash,
        pathname: current.pathname,
        ...override,
        search,
      }
      if (typeof override === 'function') {
        newLocation = override(newLocation)
      }
      if (!R.equals(current, {...current, ...newLocation})) {
        navigate(newLocation, {replace: method === 'replace'})
      }
    },
    [current, navigate, prefix],
  )

  return [params, setParams] as const
}
